<template>
    <div class="simpleAutoresponder">
        <h2>{{$t('title')}}</h2>
        <b-form-group :label="$t('text')">
            <b-textarea v-model="autoresponder.text" :state="!$v.autoresponder.text.$invalid" ></b-textarea>
            <b-form-invalid-feedback v-html="feedback(this.$v.autoresponder.text)"></b-form-invalid-feedback>
        </b-form-group>
        <small v-if="autoresponder.checklog > 0">{{$t('frequency', { time: frequencyTime })}}</small>

        <b-form-group :label="$t('dates')">
            <date-time-picker v-model="rule.datefrom" date-only optional close-on-click
                    @input="checkDateFrom"></date-time-picker>
            {{$t('dateSeparator')}}
            <date-time-picker v-model="rule.dateto" date-only optional close-on-click
                    @input="checkDateTo"></date-time-picker>

        </b-form-group>

        <b-checkbox v-model="rule.ruleenable">{{$t('enable')}}</b-checkbox>

        <b-button @click="doSave" variant="primary" :disabled="$v.$invalid || saving">
            {{$t('save')}}
        </b-button>
        <b-button @click="doReset">
            {{$t('reset')}}
        </b-button>

        <autoresponder-preview :autoresponder="autoresponder" :rule="rule"></autoresponder-preview>

        <font-awesome-icon :class="{ saveIcon: true, savingIcon: saving }" spin
                icon="spinner-third"></font-awesome-icon>
        <span class="success" v-show="saveSuccess">{{$t('saveSuccess')}}</span>
        <span class="error">{{saveError}}</span>
    </div>
</template>

<script>
import DateTimePicker from '@/components/dateTimePicker';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import axios from 'axios';
import { requiredIf } from 'vuelidate/lib/validators';
import { Duration } from 'luxon';
import AutoresponderPreview from '@/components/autoresponderPreview';

export default {
  name: 'simpleAutoresponder',
  components: { DateTimePicker, FontAwesomeIcon, AutoresponderPreview },
  i18nOptions: { keyPrefix: 'settings.autoresponders.simple' },
  data: function() {
    return {
      autoresponder: {},
      rule: { ruleenable: false, datefrom: '', dateto: '' },
      saving: false,
      saveSuccess: false,
      saveError: ''
    };
  },
  props: {
    autoresponders: Array,
    rules: Array
  },
  validations: {
    autoresponder: {
      text: {
        required: requiredIf(function() {
          return this.rule.ruleenable;
        })
      }
    }
  },
  created() {
    this.initialise();
  },
  methods: {
    initialise() {
      if (this.autoresponders.length > 0) {
        this.autoresponder = Object.assign({}, this.autoresponders[0]);
      } else {
        let period = this.$store.state.settings.userautoresponderperiod;
        if (period === -1) {
          period = this.$store.state.settings.defaultautoresponderperiod;
        }
        this.autoresponder = {
          'appendmsg': 0,
          'attachments': [],
          'checklog': period,
          'copyto': [],
          'id': 0,
          'keep': true,
          'name': 'Default',
          owner: 0,
          replyto: '',
          respondafter: 0,
          'sender': '',
          'subject': '',
          'subjectprefix': '*',
          'text': '',
          'to': ['*']
        };
      }
      if (this.rules.length > 0) {
        this.rule = Object.assign({}, this.rules[0]);
      } else {
        this.rule = {
          autoresponderenable: true,
          autoresponderid: this.autoresponder.id,
          autoresponderowner: -1,
          customfields: '',
          datefrom: '',
          datetimefrom: '',
          datetimeto: '',
          dateto: '',
          daysofweek: '0123456',
          id: 0,
          locked: false,
          name: 'Default',
          priority: 0,
          ruleenable: false,
          sender: '',
          subject: '',
          timefrom: '',
          timeto: '',
          usernumber: 0
        };
      }
      this.saveSuccess = false;
      this.saveError = '';
    },
    doReset() {
      this.initialise();
    },
    checkDateFrom() {
      if (this.rule.datefrom && (this.rule.datefrom > this.rule.dateto)) {
        this.rule.dateto = '';
      }
    },
    checkDateTo() {
      if (this.rule.dateto && (this.rule.dateto < this.rule.datefrom)) {
        this.rule.datefrom = '';
      }
    },
    doSave() {
      this.saveSuccess = false;
      this.saveError = '';
      if (this.autoresponder.id === 0) {
        this.saving = true;
        axios.post(`/rest/user/settings/autoresponders`, this.autoresponder)
          .then(d => {
            this.autoresponder = d.data;
            console.log('new autoresponder', this.autoresponder);
            this.$emit('setAutoresponder', { autoresponder: this.autoresponder });
            this.rule.autoresponderid = this.autoresponder.id;
            axios.put('/rest/user/settings/autoresponderrules', { rules: [this.rule] })
              .then(d => {
                this.$emit('setAutoresponderRules', d.data.rules);
                this.saveSuccess = true;
              })
              .catch(e => {
                this.saveError = this.$t('saveAutoresponderRuleError', { error: e.message });
              })
              .finally(() => {
                this.saving = false;
              });
          })
          .catch(e => {
            console.dir(e);
            this.saveError = this.$t('saveAutoresponderError', { error: e.message });
            this.saving = false;
          });
      } else {
        this.saving = true;
        axios.put(`/rest/user/settings/autoresponders/${this.autoresponder.id}`, this.autoresponder)
          .then(d => {
            this.$emit('setAutoresponder', { autoresponder: this.autoresponder });
            axios.put('/rest/user/settings/autoresponderrules', { rules: [this.rule] })
              .then(d => {
                this.$emit('setAutoresponderRules', d.data.rules);
                this.saveSuccess = true;
              })
              .catch(e => {
                this.saveError = this.$t('saveAutoresponderRuleError', { error: e.message });
              })
              .finally(() => {
                this.saving = false;
              });
          })
          .catch(e => {
            console.dir(e);
            this.saveError = this.$t('saveAutoresponderError', { error: e.message });
            this.saving = false;
          });
      }
    },
    feedback(validInfo) {
      if (validInfo.required === false) {
        return this.$t('general:validation.required');
      }
      return '';
    }
  },
  computed: {
    frequencyTime() {
      let dur = Duration.fromObject({ hours: this.autoresponder.checklog });
      return dur.as('days');
    }
  }
};
</script>

<style lang="scss" scoped>
    @import '../assets/bootstrap-style.scss';

</style>
